import React from "react";
import "./shell/theme/themes.scss";
import { ThemeProvider } from "./shell/theme/ThemeProvider";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { AuthProvider } from "./shell/auth/AuthProvider";
import { isMsalPopupWindow } from "./shell/auth/msalConfig";
import { RootErrorBoundary, recoverFromChunkError } from "./shell/errors/RootErrorBoundary";
import "./styles.css";
import "leaflet/dist/leaflet.css";

// A chunk that vanished in the last deploy can surface three ways, and only
// one of them reaches the error boundary below:
//
//   vite:preloadError   Vite's preload helper failing ahead of the import
//   unhandledrejection  an import() rejecting outside a render pass
//   a render throw      React.lazy — this is what the boundary catches
//
// All three mean the same thing and take the same recovery, so wire them all.
// recoverFromChunkError ignores everything that is not a missing chunk, so
// neither listener can reload the page on an ordinary failed request.
window.addEventListener("vite:preloadError", (e) => {
  if (recoverFromChunkError((e as unknown as { payload?: unknown }).payload)) {
    e.preventDefault();
  }
});

window.addEventListener("unhandledrejection", (e) => {
  recoverFromChunkError(e.reason);
});

// Drop the cache-busting marker hardReload() added, so it never sits in the
// address bar or in a bookmark.
try {
  const u = new URL(window.location.href);
  if (u.searchParams.has("_r")) {
    u.searchParams.delete("_r");
    window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
  }
} catch { /* best effort */ }

// A Microsoft sign-in popup lands here with the answer in the URL hash. It must
// not boot the app (see isMsalPopupWindow): the window that opened it reads the
// hash and closes this one.
if (isMsalPopupWindow()) {
  document.body.textContent = "Completing sign-in…";
} else {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <RootErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <ThemeProvider><App /></ThemeProvider>
          </BrowserRouter>
        </AuthProvider>
      </RootErrorBoundary>
    </React.StrictMode>
  );
}
